import { AccountModel, DialogHandler, TAccountLimits, TBrand, UserModel, getNestedProperty } from "@cakemail-org/ui-components-v2"
import { t } from "i18next"
import { goToBilling } from "./billing"

//Current feature flags
export enum EFeatureFlags {
    assetManagement = "assetManagementEnabled",
    beeRSS = "beeRSS",
    disabledPlanLimits = "disabledPlanLimits",
    emailAPI = "emailAPIEnabled",
    mfa = "mfaEnabled",
    newMenu = "showNewMenu",
    organisation = "multiAccountEnabled",
    stripePortal = "stripe_portal_access",

    indiegraf = "client_config.indiegrafIntegration.enabled",
    prismatic = "client_config.prismatic.enabled",
    intercomClassname = "client_config.intercom_classname",
    verisoul = "client_config.verisoul.enabled",
    verisoulDryRun = "client_config.verisoul.dryrun",
    yaplaCMS = "client_config.yaplaCms.calendarEventsEnabled",
    zendeskLoginAccess = "client_config.zendesk_config.enabled"
}

//Current Page Customization flags
export enum EPageCustomization {
    // Automations
    automationsDisabled = "page_customization.automations.disabled",

    // Campaigns
    campaignCreateGoesToOverview = "page_customization.campaigns.createGoesToOverview",

    // Email API
    emailAPIGuideEnabled = "page_customization.navBar.emailAPI.guideEnabled",

    // Forms
    formsDisabled = "page_customization.forms.disabled",

    // Auth
    signIn = "page_customization.sign_in",
    signUp = "page_customization.sign_up",

    // Template Browser
    templateBrowser = "page_customization.templateBrowser",

    // Billing
    billingCycle = "page_customization.navBar.accountUsage.disableBillingCycle",

    // Contacts
    segmentDropdown = "page_customization.contacts.disableSegmentDropdown",

    // Users
    userDeleteDisabled = "page_customization.users.delete_disabled",
    userCreateDisabled = "page_customization.users.create_disabled"
}

export function hasPageElementAccess({ pageElement, partnerBrand, userBrand }: { pageElement: EPageCustomization, partnerBrand: TBrand, userBrand?: TBrand }) {
    return hasFeatureFlag({ flag: pageElement as any, partnerBrand, userBrand })
}

export function hasFeatureFlag({ flag, onByDefault = false, partnerBrand, userBrand }: { flag: EFeatureFlags; onByDefault?: boolean; partnerBrand: TBrand; userBrand?: TBrand }) {
    let nestedFlag = flag.split(".")
    let resellerEnabled = undefined
    let accountEnabled = undefined

    if (nestedFlag?.length > 1) {
        resellerEnabled = getNestedProperty(partnerBrand, flag)
        if (userBrand) {
            accountEnabled = getNestedProperty(userBrand, flag)
        }
    } else {
        resellerEnabled = partnerBrand?.config?.[flag]
        if (userBrand) {
            accountEnabled = userBrand?.config?.[flag]
        }

    }
    if (accountEnabled === undefined) {
        accountEnabled = false
    }
    if (resellerEnabled === undefined) {
        resellerEnabled = false
    }

    if (onByDefault) {
        return accountEnabled !== false || resellerEnabled !== false
    }
    return accountEnabled || resellerEnabled
}


// Billing Feature functions and enums

export enum EPlanType {
    free = "free",
    growth = "growth",
    premium = "premium"
}

export enum EPlanLimitsFeatures {
    lists = "lists",
    users = "users",
    campaign_blueprints = "campaign_blueprints",
    automation_conditions = "automation_conditions",
    use_campaign_blueprints = "use_campaign_blueprints",
    use_automation_conditions = "use_automation_conditions",
    use_automations = "use_automations",
    use_automation_custom_webhook = "use_automation_customwebhooks",
    use_html_editor = "use_html_editor",
    use_contact_export = "use_contact_export",
    use_custom_merge_tag = "use_custom_merge_tags",
    use_list_redirection = "use_list_redirection",
    use_behavioral_segmentation = "use_behavioral_segmentation",
    insert_reseller_logo = "insert_reseller_logo",
    use_ab_split = "use_ab_split",
    use_tags = "use_tags",
    use_tags_in_automation = "use_tags_in_automation",
    use_smart_email_resource = "use_smart_email_resource",
    use_email_api = "use_email_api",
    use_campaigns_reports_export = "use_campaigns_reports_export",
    use_suppressed_emails_export = "use_suppressed_emails_export"
}

// Simple no billing plan type detection
// TODO : May have to load billing plan at one point
export function getPlanType(accountLimits: TAccountLimits) {
    if (accountLimits?.use_automation_conditions || accountLimits?.use_ab_split) {
        return EPlanType.premium
    } else if (accountLimits?.use_campaign_blueprints || accountLimits?.use_html_editor) {
        return EPlanType.growth
    }
    return EPlanType.free
}

export function getPlanLimitsDialogText({ name, partnerBrand, account, count }: { name: EPlanLimitsFeatures, partnerBrand?: TBrand, account: AccountModel, count?: number }) {
    const planType = getPlanType(account.usage_limits)


    const texts = {
        [EPlanLimitsFeatures.lists]: {
            title: t("", {
                count: count,
                resellerName: partnerBrand?.name,
                planType: planType || "",
            }),
            desc: t(""),
            support: t(""),
        },
        [EPlanLimitsFeatures.use_campaigns_reports_export]: {
            title: t("feature-access-dialog-campaign-export-title", {
                count: count,
                resellerName: partnerBrand?.name,
                planType: planType || "",
            }),
            desc: t("feature-access-dialog-campaign-export-desc"),
        },
        [EPlanLimitsFeatures.use_suppressed_emails_export]: {
            title: t("feature-access-dialog-suppressed-emails-export-title", {
                count: count,
                resellerName: partnerBrand?.name,
                planType: planType || "",
            }),
            desc: t("feature-access-dialog-suppressed-emails-export-desc"),
        },
        [EPlanLimitsFeatures.use_contact_export]: {
            title: t("feature-access-dialog-contacts-export-title", {
                count: count,
                resellerName: partnerBrand?.name,
                planType: planType || "",
            }),
            desc: t("feature-access-dialog-contacts-export-desc"),
        },
    }

    return texts[name as keyof typeof texts]
}


export function showPlanLimitsDialog(
    {
        name,
        account,
        user,
        partnerBrand,
        options
    }: {
        name: EPlanLimitsFeatures,
        account: AccountModel,
        user: UserModel,
        partnerBrand: TBrand,
        options?: {
            title: string,
            desc: string,
            support: string,
            primaryBtnText: string,
            primaryBtnOnClick: () => void,
            secondaryBtnText: string,
            secondaryBtnOnClick: () => void,
        }
    }
) {
    const texts = getPlanLimitsDialogText({ name, partnerBrand, account })

    // since we use same feature flag for multiple features
    let billingFeature = name
    if (name === EPlanLimitsFeatures.use_suppressed_emails_export || EPlanLimitsFeatures.use_contact_export) {
        billingFeature = EPlanLimitsFeatures.use_contact_export
    }

    const mainFunction = () => {
        goToBilling({ account, user, brand: partnerBrand })
    }

    const showDialog = !account.hasAccessTo({ feature: billingFeature as keyof TAccountLimits, partnerBrand })

    if (showDialog) {
        DialogHandler.show({
            isExpress: true,
            className: "limitFeatureDialog",
            title: options?.title ? options.title : texts.title,
            content: options?.desc ? options.desc : texts.desc,
            primaryBtnText: options?.primaryBtnText ? options.primaryBtnText : t("general-upgrade"),
            primaryBtnOnClick: options?.primaryBtnOnClick ? options.primaryBtnOnClick : mainFunction,
            secondaryBtnText: options?.secondaryBtnText ? options.secondaryBtnText : t("general-cancel"),
            secondaryBtnOnClick: options?.secondaryBtnOnClick ? options.secondaryBtnOnClick : undefined,
        })
    }

    return showDialog
}

